import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { GridStockService } from '../../../item-details/grid-stock.service';
import {
    canEditPriceInM3,
    clampDiscountPercent,
    effectiveShippedQty,
    hasPersistedShipQty,
    isQuantityAllowedByDecimalPlaces,
    parseLocaleDecimal,
    qtyDisplayFormat,
    resolveLineNoDecPl,
    restrictDecimalInputValue,
    syncShippedQuantityFromOrdered
} from '../../../item-details/item-utils';
import { isMarginApprovalEnabledForDivision, syncLineMarginFlags, showLineMarginColumns, formatMarginPercent, buildLineMarginTooltip } from '../../../item-details/margin-approval.utils';
import { MarginValidationService } from '../../../services/margin-validation.service';
import { RulesService } from '../../../services/rules.service';
import { InMemoryStorageService } from '../../../services/in-memory-storage.service';
import { ItemDetailsService } from '../../../services/item-details.service';
import { PRE_POST_TEXT_TYPES, PrePostWizardResult, PrepostTextWizardComponent } from '../../../shared/prepost-text-wizard/prepost-text-wizard.component';
import { applyPerLineEditForParity, buildSummaryFromLines, sortRowsLatestFirst } from '../../adapters/item-details-v2.adapter';
import { V2OrderLine, V2OrderSummary } from '../../models/item-details-v2.models';

@Component({
    selector: 'app-v2-lines-grid',
    standalone: true,
    imports: [CommonModule, FormsModule, TableModule, ConfirmDialogModule, TooltipModule, PrepostTextWizardComponent, InputTextModule, ButtonModule, CheckboxModule],
    providers: [ConfirmationService, GridStockService],
    template: `
        <p-confirmDialog></p-confirmDialog>
        <div
            class="v2-grid-wrap"
            [class.v2-wrap--active-edit]="hasAnyEditVisual()"
            [class.v2-wrap--bulk-edit]="bulkEditMode || priceEditMode"
        >
            <div class="v2-grid-toolbar" [class.v2-grid-toolbar--collapsed]="!searchExpanded">
                <ng-container *ngIf="!searchExpanded; else expandedSearch">
                    <button
                        type="button"
                        pButton
                        icon="pi pi-search"
                        class="p-button-text p-button-sm v2-grid-search-icon-btn"
                        (click)="expandSearch()"
                        aria-label="Search lines"
                        pTooltip="Search lines"
                        tooltipPosition="top"
                    ></button>
                </ng-container>
                <ng-template #expandedSearch>
                    <span class="v2-grid-search">
                        <input
                            #v2GridSearchInput
                            pInputText
                            type="text"
                            [(ngModel)]="globalFilterText"
                            placeholder="Search lines"
                            (keydown)="onSearchKeydown($event)"
                        />
                    </span>
                    <button
                        type="button"
                        pButton
                        label="Clear"
                        class="p-button-text p-button-sm v2-grid-clear-btn"
                        (click)="clearGlobalFilter()"
                        [disabled]="!globalFilterText"
                    ></button>
                    <button
                        type="button"
                        pButton
                        icon="pi pi-times"
                        class="p-button-text p-button-sm v2-grid-search-close-btn"
                        (click)="collapseSearch()"
                        aria-label="Close search"
                        pTooltip="Close search"
                        tooltipPosition="top"
                    ></button>
                </ng-template>
            </div>
            <p-table [value]="displayRows" [tableStyle]="{ 'min-width': '100%' }" styleClass="p-datatable-sm" [scrollable]="true" [scrollHeight]="tableScrollHeight">
                <ng-template pTemplate="header">
                    <tr>
                        <th class="v2-col-info-select" aria-label="Select line for item information"></th>
                        <th>L#</th>
                        <th>Item No</th>
                        <th class="v2-col-desc">Item Description</th>
                        <th class="v2-col-m3stat">Ln stat</th>
                        <th class="v2-col-qty">Ordered</th>
                        <th class="v2-col-qty">Shipped</th>
                        <th class="v2-col-rate">Rate</th>
                        <th *ngIf="division !== '400'" class="v2-col-disc">Disc %</th>
                        <th>Net Amount</th>
                        <th *ngIf="showMarginColumns" class="v2-col-margin-act">Margin %</th>
                        <th *ngIf="showMarginColumns" class="v2-col-margin-req">MCM %</th>
                        <th>VAT (%)</th>
                        <th>Total</th>
                        <th class="v2-col-stock">Aval stock</th>
                        <th class="v2-col-actions">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr
                        [attr.data-v2idx]="rowIndex"
                        [class.v2-row--editing]="isEditingRow(row) || isPriceEditingRow(row)"
                        [class.v2-row--copy-error]="shouldShowCopyOrderError(row)"
                        [class.margin-violation-row]="row.marginBelowMinimum"
                        [class.v2-row--selectable]="!!row?.itemNumber"
                        [class.v2-row--selected]="isLineSelected(row)"
                        [class.v2-row--ln-stat-cancelled]="isLnStat99(row)"
                        title="Double-click for item information"
                        (dblclick)="onLineInfoDblClick(row)"
                    >
                        <td class="v2-col-info-select" (click)="$event.stopPropagation()">
                            <p-checkbox
                                *ngIf="row?.itemNumber"
                                [binary]="true"
                                [ngModel]="isLineSelected(row)"
                                (onChange)="onInfoCheckboxChange($event.checked, row)"
                                [inputId]="'v2-info-' + lineInfoKey(row)"
                            />
                        </td>
                        <td>{{ row.lineNumber }}</td>
                        <td>{{ row.itemNumber }}</td>
                        <td class="v2-col-desc">
                            <span class="item-name-cell">
                                <span class="v2-desc-tail">{{ row.itemName }}</span>
                                <i
                                    *ngIf="row.marginBelowMinimum"
                                    class="pi pi-exclamation-triangle margin-line-badge"
                                    pTooltip="Contribution margin is below the minimum required threshold"
                                    tooltipPosition="top"
                                    aria-label="Contribution margin below minimum required threshold"
                                ></i>
                            </span>
                        </td>
                        <td class="v2-col-m3stat">{{ formatLnStat(row) }}</td>
                        <td class="v2-col-qty">
                            <ng-container *ngIf="isEditingRow(row) && !isOrderedQtyLocked(row); else qtyStatic">
                                <input
                                    type="text"
                                    inputmode="decimal"
                                    autocomplete="off"
                                    [(ngModel)]="row.quantity"
                                    class="v2-cell-input v2-qty-input"
                                    [ngModelOptions]="{ standalone: true }"
                                    (input)="restrictQtyDecimals($event, row)"
                                    (focus)="onQtyFocus($event, row)"
                                    (blur)="runQuantityBlur(row, rowIndex)"
                                    (keydown)="onQtyKeydown($event, row, rowIndex)"
                                />
                            </ng-container>
                            <ng-template #qtyStatic>{{
                                isCreditNoteOrder
                                    ? (row.quantity | number: getQtyDisplayFormat(row))
                                    : (row.quantity < 0.01 ? 0.01 : (row.quantity | number: getQtyDisplayFormat(row)))
                            }}</ng-template>
                        </td>
                        <td class="v2-col-qty">{{
                            getShippedDisplayQty(row) | number: getShippedQtyDisplayFormat(row)
                        }}</td>
                        <td class="v2-col-rate">
                            <ng-container *ngIf="isEditingPriceFields(row); else rateStatic">
                                <input
                                    type="text"
                                    inputmode="decimal"
                                    autocomplete="off"
                                    [(ngModel)]="row.rate"
                                    class="v2-cell-input v2-rate-input"
                                    [ngModelOptions]="{ standalone: true }"
                                    (input)="restrictRateDecimals($event, row)"
                                    (blur)="onRateInputBlur(row, rowIndex)"
                                    (keydown)="onRateKeydown($event, row, rowIndex)"
                                    (focus)="onRateFocus($event, row)"
                                />
                            </ng-container>
                            <ng-template #rateStatic>{{ row.rate | number: '1.2-2' }}</ng-template>
                            <div *ngIf="row.priceUpdateStatus && row.priceUpdateStatus !== 'idle'" class="v2-price-badge" [ngClass]="'v2-price-badge--' + row.priceUpdateStatus" [pTooltip]="row.priceUpdateMessage || ''" tooltipPosition="top">
                                {{ formatPriceUpdateStatus(row) }}
                            </div>
                        </td>
                        <td *ngIf="division !== '400'" class="v2-col-disc">
                            <ng-container *ngIf="isEditingPriceFields(row); else discStatic">
                                <input
                                    type="text"
                                    inputmode="decimal"
                                    autocomplete="off"
                                    [(ngModel)]="row.discount"
                                    class="v2-cell-input v2-disc-input"
                                    [ngModelOptions]="{ standalone: true }"
                                    (input)="restrictDiscDecimals($event, row)"
                                    (blur)="onDiscInputBlur(row, rowIndex)"
                                    (keydown)="onDiscKeydown($event, row, rowIndex)"
                                    (focus)="onDiscFocus($event, row)"
                                />
                            </ng-container>
                            <ng-template #discStatic>{{ row.discount | number: '1.2-2' }}</ng-template>
                        </td>
                        <td>{{ row.amountAfterDiscount | number: '1.2-2' }}</td>
                        <td *ngIf="showMarginColumns" class="v2-col-margin-act">
                            <span
                                class="margin-actual-cell"
                                [class.margin-actual--below]="row.marginBelowMinimum"
                                [class.margin-actual--ok]="!row.marginBelowMinimum && row.calculatedMargin != null"
                                [pTooltip]="getLineMarginTooltip(row)"
                                tooltipPosition="top"
                            >{{ formatMarginPercent(row.calculatedMargin) }}</span>
                        </td>
                        <td *ngIf="showMarginColumns" class="v2-col-margin-req">
                            {{ formatMarginPercent(row.minimumMargin ?? row.itemMcm) }}
                        </td>
                        <td>{{ row.vat | number: '1.0-2' }}</td>
                        <td>{{ row.amountAfterVat | number: '1.2-2' }}</td>
                        <td class="v2-col-stock">
                            <ng-container *ngIf="!shouldHideStockInfo(row) && shouldShowCopyOrderError(row); else normalStock">
                                <span class="v2-stock-error" [pTooltip]="row.copyOrderStockError" tooltipPosition="top">
                                    <i class="pi pi-exclamation-triangle" *ngIf="shouldShowStockErrorIcon(row.copyOrderStockError)"></i>
                                    <span class="v2-stock-error-text">{{ getCompactStockError(row.copyOrderStockError) }}</span>
                                </span>
                            </ng-container>
                            <ng-template #normalStock>
                                <ng-container *ngIf="!shouldHideStockInfo(row) && shouldShowStockVerificationError(row); else availableStock">
                                    <span class="v2-stock-error" [pTooltip]="getStockVerificationMessage(row)" tooltipPosition="top">
                                        <i class="pi pi-exclamation-triangle"></i>
                                        <span class="v2-stock-error-text">Cannot verify stock</span>
                                    </span>
                                </ng-container>
                                <ng-template #availableStock>
                                <span *ngIf="!shouldHideStockInfo(row) && row.avlStock != null" class="v2-stock-ok">Avl: {{ getDisplayedAvlStock(row) }}</span>
                                </ng-template>
                            </ng-template>
                        </td>
                        <td>
                            <a
                                class="v2-action-link"
                                href="javascript:void(0)"
                                *ngIf="!bulkEditMode && !isEditingRow(row) && !readOnly && !isReadOnly"
                                (click)="$event.stopPropagation(); startEdit(row)"
                                >Edit</a
                            >
                            <a
                                class="v2-action-link"
                                href="javascript:void(0)"
                                *ngIf="!bulkEditMode && isEditingRow(row) && !readOnly && !isReadOnly"
                                (click)="$event.stopPropagation(); saveEdit(row, rowIndex)"
                                >Done</a
                            >
                            <span *ngIf="!readOnly && !isReadOnly && !bulkEditMode"> | </span>
                            <a
                                class="v2-action-link v2-danger"
                                href="javascript:void(0)"
                                *ngIf="!readOnly && !isReadOnly"
                                (click)="$event.stopPropagation(); confirmDelete(row)"
                                >Del</a
                            >
                            <ng-container *ngIf="canShowLineTextActions() && !bulkEditMode && !isEditingRow(row)">
                                <span *ngIf="!readOnly && !isReadOnly"> | </span>
                                <a class="v2-action-link" href="javascript:void(0)" (click)="$event.stopPropagation(); openLineTextWizard(row, LINE_TEXT_TYPES.PRE)">Pre</a>
                                <span> / </span>
                                <a class="v2-action-link" href="javascript:void(0)" (click)="$event.stopPropagation(); openLineTextWizard(row, LINE_TEXT_TYPES.POST)">Post</a>
                            </ng-container>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </div>
        <app-prepost-text-wizard [(visible)]="showLineTextWizardDialog" [textType]="selectedLineTextType" (complete)="onLinePrePostComplete($event)"></app-prepost-text-wizard>
    `,
    styles: [
        `
            .v2-grid-wrap {
                border: 1px solid var(--border-color, var(--p-surface-border));
                border-radius: 0.75rem;
                overflow: hidden;
                background: var(--surface-card, var(--p-surface-0));
                position: relative;
                transition:
                    border-color 0.15s ease,
                    box-shadow 0.15s ease;
            }
            .v2-wrap--active-edit {
                border-color: var(--primary-color);
                box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary-color) 35%, transparent);
            }
            .v2-wrap--bulk-edit {
                background: color-mix(in srgb, var(--primary-color) 8%, var(--surface-card, var(--p-surface-0)));
            }
            .v2-price-badge {
                margin-top: 0.15rem;
                font-size: 0.68rem;
                font-weight: 700;
                line-height: 1.1;
            }
            .v2-price-badge--updating { color: var(--text-secondary, #6b7280); }
            .v2-price-badge--updated { color: #15803d; }
            .v2-price-badge--failed { color: #b91c1c; }
            .v2-price-badge--inconsistency { color: #c2410c; }
            .v2-grid-toolbar {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 0.5rem;
                padding: 0.55rem 0.6rem;
                border-bottom: 1px solid var(--border-color, var(--p-surface-border));
            }
            .v2-grid-toolbar--collapsed {
                position: absolute;
                top: 0.38rem;
                right: 0.45rem;
                z-index: 4;
                padding: 0;
                border-bottom: none;
                background: transparent;
                width: auto;
                min-height: 0;
                gap: 0;
            }
            .v2-grid-search {
                flex: 1 1 auto;
                min-width: 0;
                width: 100%;
                display: block;
            }
            .v2-grid-search input {
                width: 100%;
                box-sizing: border-box;
            }
            .v2-grid-clear-btn {
                flex: 0 0 auto;
                margin-left: auto;
            }
            .v2-grid-search-close-btn {
                flex: 0 0 auto;
            }
            .v2-grid-search-icon-btn {
                flex: 0 0 auto;
                width: 1.9rem;
                height: 1.9rem;
            }
            :host-context(.app-dark) .v2-wrap--bulk-edit,
            :host-context(html.app-dark) .v2-wrap--bulk-edit {
                background: color-mix(in srgb, var(--primary-color) 14%, var(--p-surface-900, #0b0b0b));
            }
            :host ::ng-deep .v2-grid-wrap .p-datatable-thead > tr > th {
                background: var(--p-surface-100, #f4f5f7);
                color: var(--p-text-color, #1f2937);
                border-bottom: 1px solid var(--p-surface-border, #dfe3e8);
                border-right: 1px solid var(--p-surface-border, #dfe3e8);
                padding: 0.56rem 0.6rem;
                font-size: 0.9rem;
                font-weight: 600;
                white-space: nowrap;
                letter-spacing: 0;
                text-transform: none;
                line-height: 1.2;
            }
            :host ::ng-deep .v2-grid-wrap .p-datatable-thead > tr > th:last-child {
                border-right: none;
            }
            :host ::ng-deep .v2-grid-wrap .p-datatable-table {
                min-width: 860px;
            }
            :host-context(.app-dark) ::ng-deep .v2-grid-wrap .p-datatable-thead > tr > th,
            :host-context(html.app-dark) ::ng-deep .v2-grid-wrap .p-datatable-thead > tr > th {
                background: var(--p-surface-800, #1f2937);
                color: var(--p-surface-0, #f8fafc);
                border-bottom-color: var(--p-surface-700, #374151);
                border-right-color: var(--p-surface-700, #374151);
            }
            :host ::ng-deep .v2-grid-wrap .p-datatable-tbody > tr > td {
                border-top: 1px solid var(--border-color, var(--p-surface-border));
                padding: 0.42rem 0.45rem;
                color: var(--text-color, var(--p-text-color));
                font-size: 0.95rem;
                line-height: 1.1;
            }
            tr.v2-row--editing td {
                background: color-mix(in srgb, var(--primary-color) 16%, transparent) !important;
            }
            tr.v2-row--selectable {
                cursor: pointer;
            }
            tr.v2-row--selected td {
                background: color-mix(in srgb, var(--primary-color) 12%, transparent) !important;
            }
            .v2-col-info-select {
                width: 2.4rem;
                min-width: 2.4rem;
                max-width: 2.4rem;
                text-align: center;
                padding-left: 0.35rem;
                padding-right: 0.35rem;
            }
            .v2-cell-input {
                width: 100%;
                border: 1px solid var(--border-color, var(--p-surface-border));
                border-radius: var(--p-inputtext-border-radius, 6px);
                padding: 0.22rem 0.36rem;
                background: var(--surface-card, var(--p-surface-0));
                color: var(--text-color, var(--p-text-color));
                min-height: 1.7rem;
            }
            .v2-cell-input:focus {
                border-color: var(--primary-color);
                box-shadow: 0 0 0 0.12rem color-mix(in srgb, var(--primary-color) 25%, transparent);
            }
            /* Hide native number-input spinner arrows (Chrome/Safari/Edge/Firefox). */
            .v2-cell-input[type='number'] {
                appearance: textfield;
                -moz-appearance: textfield;
            }
            .v2-cell-input[type='number']::-webkit-outer-spin-button,
            .v2-cell-input[type='number']::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            .v2-action-link {
                color: var(--primary-color);
                text-decoration: none;
                font-weight: 600;
                font-size: 0.86rem;
                margin-right: 0.15rem;
            }
            .v2-danger {
                color: #dc2626;
            }
            .v2-row--copy-error td {
                background: color-mix(in srgb, #dc2626 7%, transparent);
            }
            .v2-col-stock {
                min-width: 6.6rem;
                max-width: 7.4rem;
            }
            .v2-col-desc {
                min-width: 17rem;
                max-width: 20rem;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .v2-desc-tail {
                display: block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                direction: rtl;
                text-align: left;
            }
            .v2-col-qty {
                min-width: 5rem;
                max-width: 5.8rem;
            }
            .v2-col-rate {
                min-width: 6.6rem;
                max-width: 7.6rem;
            }
            .v2-col-disc {
                min-width: 4.2rem;
                max-width: 4.8rem;
            }
            .v2-col-margin-act {
                min-width: 4.6rem;
                max-width: 5.2rem;
                white-space: nowrap;
            }
            .v2-col-margin-req {
                min-width: 3.8rem;
                max-width: 4.4rem;
                white-space: nowrap;
            }
            .v2-col-m3stat {
                min-width: 3.6rem;
                max-width: 4.2rem;
                text-align: center;
            }
            /* Proper red for cancelled line status 99 — full row, light + dark */
            :host ::ng-deep tr.v2-row--ln-stat-cancelled > td {
                background: #ef4444 !important;
                color: #ffffff;
            }
            :host-context(.app-dark) ::ng-deep tr.v2-row--ln-stat-cancelled > td,
            :host-context(html.app-dark) ::ng-deep tr.v2-row--ln-stat-cancelled > td {
                background: #dc2626 !important;
                color: #ffffff;
            }
            .v2-col-actions {
                white-space: nowrap;
                min-width: 8.6rem;
                max-width: 10rem;
            }
            .v2-stock-error {
                color: #dc2626;
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
                font-size: 0.82rem;
                line-height: 1.2;
            }
            .v2-stock-error-text {
                display: inline-block;
                max-width: 8.6rem;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .v2-stock-ok {
                font-size: 0.82rem;
                color: var(--text-secondary, var(--p-text-muted-color));
            }
            @media (max-width: 1400px) {
                .v2-col-desc {
                    min-width: 14.2rem;
                    max-width: 16rem;
                }
                .v2-col-stock {
                    min-width: 6rem;
                    max-width: 6.8rem;
                }
                .v2-col-qty {
                    min-width: 4.6rem;
                    max-width: 5.2rem;
                }
                .v2-col-rate {
                    min-width: 6rem;
                    max-width: 6.9rem;
                }
                .v2-col-disc {
                    min-width: 3.8rem;
                    max-width: 4.3rem;
                }
                .v2-col-m3stat {
                    min-width: 3.2rem;
                    max-width: 3.8rem;
                }
                .v2-stock-error-text {
                    max-width: 5.3rem;
                }
                .v2-col-actions {
                    min-width: 7.8rem;
                    max-width: 8.5rem;
                    font-size: 0.8rem;
                }
                .v2-action-link {
                    font-size: 0.8rem;
                    margin-right: 0.1rem;
                }
            }
            @media (max-width: 1200px) {
                .v2-col-desc {
                    min-width: 12rem;
                    max-width: 13.5rem;
                }
                .v2-col-stock {
                    min-width: 5.6rem;
                    max-width: 6.3rem;
                }
                .v2-col-qty {
                    min-width: 4.2rem;
                    max-width: 4.8rem;
                }
                .v2-col-rate {
                    min-width: 5.4rem;
                    max-width: 6.2rem;
                }
                .v2-col-disc {
                    min-width: 3.5rem;
                    max-width: 4rem;
                }
                .v2-col-m3stat {
                    min-width: 3rem;
                    max-width: 3.6rem;
                }
                .v2-stock-error-text {
                    max-width: 4.8rem;
                }
                .v2-col-actions {
                    min-width: 7.2rem;
                    max-width: 8rem;
                }
            }
            @media (max-width: 768px) {
                .v2-grid-wrap {
                    overflow-x: auto;
                    overflow-y: visible;
                    -webkit-overflow-scrolling: touch;
                    max-width: 100%;
                }
                :host ::ng-deep .v2-grid-wrap .p-datatable-wrapper {
                    overflow-x: auto !important;
                    max-width: 100%;
                }
                :host ::ng-deep .v2-grid-wrap .p-datatable-table {
                    min-width: 960px;
                }
                :host ::ng-deep .v2-grid-wrap .p-datatable-thead > tr > th,
                :host ::ng-deep .v2-grid-wrap .p-datatable-tbody > tr > td {
                    padding: 0.42rem 0.45rem;
                    font-size: 0.82rem;
                }
                :host ::ng-deep .v2-grid-wrap .v2-col-desc {
                    max-width: 11rem;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        `
    ]
})
export class V2LinesGridComponent implements OnInit, OnChanges, OnDestroy {
    @Input() itemList: V2OrderLine[] = [];
    @Input() isCreditNoteOrder = false;
    @Input() division = '';
    @Input() readOnly = false;
    @Input() isReadOnly = false;
    /** Order has M3 reference order id (synced). */
    @Input() hasReferenceOrder = false;
    /** Parent toggled "Edit Prices" — unlock Rate/Disc only on eligible lines. */
    @Input() priceEditMode = false;
    /** Header gate passed: missing line m3LineStatus still allows Rate/Disc. */
    @Input() orderPriceEditable = false;
    @Input() company = '';
    @Input() warehouse = '';
    @Input() customerId = '';
    @Input() defaultPriceList = '';
    @Input() deletedItems: Map<string, any[]> = new Map();
    @Input() isManagerOrSuperUser = false;
    @Input() orderNumber = '';
    @Input() orderMovingStatus = '';
    @Input() selectedLineKey = '';

    bulkEditMode = false;
    /** Viewport-based inner scroll height; wider subtract on desktop, tighter on phone. */
    tableScrollHeight = 'calc(100vh - 16rem)';

    @Output() itemListChange = new EventEmitter<V2OrderLine[]>();
    @Output() deleteRow = new EventEmitter<V2OrderLine>();
    /** Zero-qty delete path uses itemList index (same as V1 item-table). */
    @Output() deleteByItemListIndex = new EventEmitter<number>();
    @Output() editingStateChange = new EventEmitter<boolean>();
    @Output() bulkEditChange = new EventEmitter<boolean>();
    @Output() summaryChange = new EventEmitter<V2OrderSummary>();
    @Output() rateBlur = new EventEmitter<V2OrderLine>();
    @Output() discountBlur = new EventEmitter<V2OrderLine>();
    @Output() marginValidationFailed = new EventEmitter<{ actual: number; required: number; itemName?: string }>();
    @Output() lineSelectedForInfo = new EventEmitter<V2OrderLine>();

    private editingKeys = new Set<string>();
    private readonly oldQtyByRow = new Map<string, number>();
    private readonly oldRateByRow = new Map<string, number>();
    private readonly oldDiscountByRow = new Map<string, number>();
    private ruleActiveForDivision = false;
    private rulesSub?: Subscription;
    globalFilterText = '';
    searchExpanded = false;
    @ViewChild('v2GridSearchInput') private v2GridSearchInput?: ElementRef<HTMLInputElement>;
    showLineTextWizardDialog = false;
    selectedLineTextType = '';
    selectedLineForText: V2OrderLine | null = null;
    readonly LINE_TEXT_TYPES = {
        PRE: PRE_POST_TEXT_TYPES.PRE,
        POST: PRE_POST_TEXT_TYPES.POST
    } as const;

    constructor(
        private readonly host: ElementRef<HTMLElement>,
        private readonly confirmationService: ConfirmationService,
        private readonly gridStock: GridStockService,
        private readonly rulesService: RulesService,
        private readonly messageService: MessageService,
        private readonly inMemoryStorage: InMemoryStorageService,
        private readonly itemDetailsService: ItemDetailsService,
        private readonly marginValidationService: MarginValidationService
    ) {}

    ngOnInit(): void {
        this.rulesSub = this.rulesService.ruleActiveForDivision$.subscribe((flag) => {
            this.ruleActiveForDivision = flag;
        });
        this.syncTableScrollHeight();
        this.syncMarginFlagsOnRows();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['itemList'] || changes['division']) {
            this.syncMarginFlagsOnRows();
        }
    }

    private syncMarginFlagsOnRows(): void {
        if (this.isCreditNoteOrder || !isMarginApprovalEnabledForDivision(this.division)) {
            return;
        }
        syncLineMarginFlags(this.marginValidationService, this.itemList as any[]);
    }

    get showMarginColumns(): boolean {
        return showLineMarginColumns(this.division, this.isCreditNoteOrder, this.inMemoryStorage, this.warehouse);
    }

    formatMarginPercent = formatMarginPercent;

    getLineMarginTooltip(row: V2OrderLine): string {
        return buildLineMarginTooltip(row, this.marginValidationService);
    }

    ngOnDestroy(): void {
        this.rulesSub?.unsubscribe();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.syncTableScrollHeight();
    }

    private syncTableScrollHeight(): void {
        if (typeof window === 'undefined') {
            return;
        }
        if (window.innerWidth <= 768) {
            this.tableScrollHeight = 'calc(100dvh - 10.5rem)';
        } else {
            this.tableScrollHeight = 'calc(100vh - 16rem)';
        }
    }

    get displayRows(): V2OrderLine[] {
        const rows = sortRowsLatestFirst(this.itemList);
        const term = (this.globalFilterText || '').trim().toLowerCase();
        if (!term) {
            return rows;
        }
        return rows.filter((row) => this.matchesGlobalFilter(row, term));
    }

    lineInfoKey(row: V2OrderLine): string {
        return `${row?.lineNumber ?? ''}-${row?.itemNumber ?? ''}`;
    }

    isLineSelected(row: V2OrderLine): boolean {
        return !!this.selectedLineKey && this.lineInfoKey(row) === this.selectedLineKey;
    }

    onInfoCheckboxChange(checked: boolean, row: V2OrderLine): void {
        if (!checked || !row?.itemNumber) {
            return;
        }
        this.lineSelectedForInfo.emit(row);
    }

    onLineInfoDblClick(row: V2OrderLine): void {
        if (!row?.itemNumber || this.isEditingRow(row)) {
            return;
        }
        this.lineSelectedForInfo.emit(row);
    }

    clearGlobalFilter(): void {
        this.globalFilterText = '';
    }

    expandSearch(): void {
        this.searchExpanded = true;
        setTimeout(() => {
            this.v2GridSearchInput?.nativeElement?.focus();
        });
    }

    collapseSearch(): void {
        this.clearGlobalFilter();
        this.searchExpanded = false;
    }

    onSearchKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.collapseSearch();
        }
    }

    hasAnyEditVisual(): boolean {
        return this.bulkEditMode || this.editingKeys.size > 0 || this.priceEditMode;
    }

    isEditingRow(row: V2OrderLine): boolean {
        return this.bulkEditMode || this.editingKeys.has(this.getRowKey(row));
    }

    /** Same row highlight as draft Edit, for synced lines that can change Rate/Disc. */
    isPriceEditingRow(row: V2OrderLine): boolean {
        return this.priceEditMode && this.readOnly && this.canEditM3Price(row);
    }

    /** Rate/discount editable in normal edit mode, or in synced price-edit mode on eligible lines. */
    isEditingPriceFields(row: V2OrderLine): boolean {
        if (this.isReadOnly) {
            return false;
        }
        if (this.isEditingRow(row)) {
            return true;
        }
        // Synced: only when user clicked Edit Prices (Qty stays locked).
        return this.isPriceEditingRow(row);
    }

    canEditM3Price(row: V2OrderLine): boolean {
        return canEditPriceInM3(row, {
            hasReferenceOrder: this.hasReferenceOrder,
            roleReadOnly: this.isReadOnly,
            orderPriceEditable: this.orderPriceEditable
        });
    }

    formatPriceUpdateStatus(row: V2OrderLine): string {
        switch (row.priceUpdateStatus) {
            case 'updating':
                return 'Updating…';
            case 'updated':
                return 'Price updated';
            case 'failed':
                return 'Update failed';
            case 'inconsistency':
                return 'M3 ok, Torna save failed';
            default:
                return '';
        }
    }

    startEdit(row: V2OrderLine): void {
        if (this.bulkEditMode) {
            return;
        }
        this.editingKeys.add(this.getRowKey(row));
        this.emitEditingAndBulkState();
    }

    confirmDelete(row: V2OrderLine): void {
        this.confirmationService.confirm({
            header: 'Delete Confirmation',
            message: 'Are you sure you want to delete this item?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => this.deleteRow.emit(row)
        });
    }

    toggleBulkEdit(): void {
        if (this.bulkEditMode) {
            this.commitBulkEdit();
            this.bulkEditMode = false;
        } else {
            this.editingKeys.clear();
            this.bulkEditMode = true;
        }
        this.emitEditingAndBulkState();
    }

    /** Exit bulk edit without committing when switching to price-edit mode. */
    exitBulkEditQuietly(): void {
        if (!this.bulkEditMode) {
            return;
        }
        this.bulkEditMode = false;
        this.editingKeys.clear();
        this.emitEditingAndBulkState();
    }

    private commitBulkEdit(): void {
        const list = this.itemList || [];
        for (const row of list) {
            Object.assign(row, applyPerLineEditForParity(row));
        }
        this.validateRowsMarginSequential(list, () => {
            for (const row of list) {
                this.rateBlur.emit(row);
                this.discountBlur.emit(row);
            }
            this.itemListChange.emit([...list]);
            this.summaryChange.emit(buildSummaryFromLines(list));
        }, () => {
            this.bulkEditMode = true;
            this.emitEditingAndBulkState();
        });
    }

    private emitEditingAndBulkState(): void {
        const editing = this.bulkEditMode || this.editingKeys.size > 0;
        this.editingStateChange.emit(editing);
        this.bulkEditChange.emit(this.bulkEditMode);
    }

    saveEdit(row: V2OrderLine, rowIndex: number): void {
        Object.assign(row, applyPerLineEditForParity(row));
        this.applyRowMarginValidationWithFetch(row, rowIndex, 'discount', undefined, undefined, undefined, (passed) => {
            if (!passed) {
                return;
            }
            this.rateBlur.emit(row);
            this.discountBlur.emit(row);
            this.editingKeys.delete(this.getRowKey(row));
            this.emitEditingAndBulkState();
            this.itemListChange.emit([...(this.itemList || [])]);
            this.summaryChange.emit(buildSummaryFromLines(this.itemList));
        });
    }

    /** V1 item-table: store qty on focus + capture originalQuantity when rules apply */
    onQtyFocus(event: FocusEvent, row: V2OrderLine): void {
        this.oldQtyByRow.set(this.getRowKey(row), row.quantity);
        this.gridStock.onQuantityFocus(row, this.ruleActiveForDivision);
        const t = event.target as HTMLInputElement;
        t?.select?.();
    }

    /** Ordered qty locked after France short-pick (ship_qty persisted). */
    isOrderedQtyLocked(row: V2OrderLine): boolean {
        return hasPersistedShipQty(row);
    }

    getShippedDisplayQty(row: V2OrderLine): number {
        return effectiveShippedQty(row);
    }

    getShippedQtyDisplayFormat(row: V2OrderLine): string {
        return qtyDisplayFormat(resolveLineNoDecPl(row, 2), this.getShippedDisplayQty(row), 2);
    }

    /** V1 item-table onQuantityBlur + stock API + validation */
    runQuantityBlur(row: V2OrderLine, displayIndex: number): void {
        if (this.isOrderedQtyLocked(row)) {
            return;
        }
        const key = this.getRowKey(row);
        const oldQty = this.oldQtyByRow.has(key) ? this.oldQtyByRow.get(key)! : row.quantity;
        const itemListIndex = this.getItemListIndex(row);
        if (itemListIndex < 0) {
            return;
        }

        const allowedDecimals = resolveLineNoDecPl(row, 2);
        if (!isQuantityAllowedByDecimalPlaces(row.quantity, allowedDecimals)) {
            row.quantity = oldQty;
            syncShippedQuantityFromOrdered(row);
            this.messageService.add({
                severity: 'error',
                summary: 'Invalid quantity',
                detail: `Invalid quantity. Maximum ${allowedDecimals} decimal places are allowed for this item.`,
                life: 4000
            });
            return;
        }

        syncShippedQuantityFromOrdered(row);

        this.gridStock.handleQuantityBlur({
            row,
            itemList: this.itemList as any[],
            itemListIndex,
            displayIndex,
            oldQty,
            company: this.company,
            division: this.division,
            warehouse: this.warehouse,
            customerId: this.customerId,
            deletedItems: this.deletedItems,
            ruleActiveForDivision: this.ruleActiveForDivision,
            isCreditNoteOrder: this.isCreditNoteOrder,
            emitTableChange: () => {
                this.itemListChange.emit([...(this.itemList || [])]);
                this.summaryChange.emit(buildSummaryFromLines(this.itemList));
            },
            deleteItemAtListIndex: (idx) => this.deleteByItemListIndex.emit(idx),
            focusQuantityAtDisplayIndex: (di) => this.focusFieldAt('qty', di),
            onAfterStockResolved: () => {
                this.applyRowMarginValidationWithFetch(row, displayIndex, 'quantity', oldQty);
            }
        });
    }

    restrictQtyDecimals(event: Event, row: V2OrderLine): void {
        const t = event.target as HTMLInputElement;
        const maxDecimals = resolveLineNoDecPl(row, 2);
        const restricted = restrictDecimalInputValue(String(t.value ?? ''), maxDecimals);
        if (restricted !== t.value) {
            t.value = restricted;
        }
        row.quantity = parseLocaleDecimal(restricted);
        syncShippedQuantityFromOrdered(row);
    }

    restrictRateDecimals(event: Event, row: V2OrderLine): void {
        const t = event.target as HTMLInputElement;
        const restricted = restrictDecimalInputValue(String(t.value ?? ''), 2);
        if (restricted !== t.value) {
            t.value = restricted;
        }
        row.rate = parseLocaleDecimal(restricted);
    }

    restrictDiscDecimals(event: Event, row: V2OrderLine): void {
        const t = event.target as HTMLInputElement;
        const restricted = restrictDecimalInputValue(String(t.value ?? ''), 2);
        if (restricted !== t.value) {
            t.value = restricted;
        }
        row.discount = parseLocaleDecimal(restricted);
    }

    getQtyDisplayFormat(row: V2OrderLine): string {
        return qtyDisplayFormat(resolveLineNoDecPl(row, 2), row?.quantity, 2);
    }

    onRateFocus(event: FocusEvent, row: V2OrderLine): void {
        this.oldRateByRow.set(this.getRowKey(row), Number(row.rate) || 0);
        this.selectAll(event);
    }

    onDiscFocus(event: FocusEvent, row: V2OrderLine): void {
        this.oldDiscountByRow.set(this.getRowKey(row), Number(row.discount) || 0);
        this.selectAll(event);
    }

    onRateInputBlur(row: V2OrderLine, rowIndex: number): void {
        /* V1 item-table blur: floor rate at 0.01 before minrate / amount / MCM margin checks */
        const rateNum = Number(row.rate);
        if (!Number.isFinite(rateNum) || rateNum < 0.01) {
            row.rate = 0.01;
        }

        /* V1 item-table validateRate (non-managers) */
        const minrate = (row as any)['minrate'];
        if (!this.isManagerOrSuperUser && minrate != null && Number(row.rate) < Number(minrate)) {
            row.rate = Number(minrate);
            const msg = `Rate must be at least ${Number(minrate).toFixed(2)}`;
            (row as any).rateError = msg;
            this.messageService.add({ severity: 'warn', summary: 'Rate', detail: msg, life: 4000 });
        } else {
            (row as any).rateError = '';
        }
        const updated = applyPerLineEditForParity(row);
        Object.assign(row, updated);
        const oldRate = this.oldRateByRow.get(this.getRowKey(row));
        this.applyRowMarginValidationWithFetch(row, rowIndex, 'rate', undefined, oldRate);
    }

    onDiscInputBlur(row: V2OrderLine, rowIndex: number): void {
        const next = clampDiscountPercent(row.discount);
        if (Number(next) !== Number(row.discount)) {
            this.messageService.add({ severity: 'warn', summary: 'Discount', detail: 'Discount cannot exceed 100.', life: 3000 });
        }
        row.discount = next;
        const updated = applyPerLineEditForParity(row);
        Object.assign(row, updated);
        const oldDiscount = this.oldDiscountByRow.get(this.getRowKey(row));
        this.applyRowMarginValidationWithFetch(row, rowIndex, 'discount', undefined, undefined, oldDiscount);
    }

    private applyRowMarginValidation(
        row: V2OrderLine,
        rowIndex: number,
        revertField: 'quantity' | 'rate' | 'discount' | null,
        oldQty?: number,
        oldRate?: number,
        oldDiscount?: number
    ): boolean {
        if (this.isCreditNoteOrder) {
            return true;
        }
        const result = this.marginValidationService.validateRowContributionMargin(row);
        if (!result) {
            return true;
        }
        if (result.isValid) {
            (row as any).marginBelowMinimum = false;
            (row as any).lineCost = this.marginValidationService.resolveLineCost(row);
            (row as any).calculatedMargin = result.actualMarginPercent;
            (row as any).minimumMargin = result.minimumMarginPercent;
            return true;
        }
        if (isMarginApprovalEnabledForDivision(this.division)) {
            (row as any).marginBelowMinimum = true;
            (row as any).calculatedMargin = result.actualMarginPercent;
            (row as any).minimumMargin = result.minimumMarginPercent;
            this.marginValidationFailed.emit({
                actual: result.actualMarginPercent,
                required: result.minimumMarginPercent,
                itemName: row.itemName
            });
            this.itemListChange.emit([...(this.itemList || [])]);
            this.summaryChange.emit(buildSummaryFromLines(this.itemList));
            return true;
        }
        if (revertField === 'quantity' && oldQty !== undefined) {
            row.quantity = oldQty;
        } else if (revertField === 'rate' && oldRate !== undefined) {
            row.rate = oldRate;
        } else if (revertField === 'discount' && oldDiscount !== undefined) {
            row.discount = oldDiscount;
        }
        Object.assign(row, applyPerLineEditForParity(row));
        this.marginValidationFailed.emit({
            actual: result.actualMarginPercent,
            required: result.minimumMarginPercent,
            itemName: row.itemName
        });
        this.itemListChange.emit([...(this.itemList || [])]);
        this.summaryChange.emit(buildSummaryFromLines(this.itemList));
        setTimeout(() => this.focusFieldAt(revertField === 'quantity' ? 'qty' : revertField === 'rate' ? 'rate' : 'disc', rowIndex), 100);
        return false;
    }

    private validateRowsMarginSequential(
        rows: V2OrderLine[],
        onAllPassed: () => void,
        onFailed: () => void
    ): void {
        let index = 0;
        const next = () => {
            if (index >= rows.length) {
                onAllPassed();
                return;
            }
            const row = rows[index];
            const displayIndex = this.findDisplayIndex(row);
            this.applyRowMarginValidationWithFetch(row, displayIndex, 'discount', undefined, undefined, undefined, (passed) => {
                if (!passed) {
                    onFailed();
                    return;
                }
                index += 1;
                next();
            });
        };
        next();
    }

    private findDisplayIndex(row: V2OrderLine): number {
        const key = this.getRowKey(row);
        const idx = this.displayRows.findIndex((r) => this.getRowKey(r) === key);
        return idx >= 0 ? idx : 0;
    }

    private applyRowMarginValidationWithFetch(
        row: V2OrderLine,
        rowIndex: number,
        field: 'quantity' | 'rate' | 'discount',
        oldQty?: number,
        oldRate?: number,
        oldDiscount?: number,
        onComplete?: (passed: boolean) => void
    ): void {
        // Cost/MCM are unit-level and loaded at copy/excel/add — qty change does not re-fetch ItemDetails.
        const passed = this.applyRowMarginValidation(row, rowIndex, field, oldQty, oldRate, oldDiscount);
        if (onComplete) {
            onComplete(passed);
            return;
        }
        if (!passed) {
            return;
        }
        const updated = applyPerLineEditForParity(row);
        Object.assign(row, updated);
        if (field === 'rate') {
            this.rateBlur.emit(updated);
        } else if (field === 'discount') {
            this.discountBlur.emit(updated);
        }
        this.itemListChange.emit([...(this.itemList || [])]);
        this.summaryChange.emit(buildSummaryFromLines(this.itemList));
    }

    selectAll(event: FocusEvent): void {
        const t = event.target as HTMLInputElement;
        t?.select?.();
    }

    onQtyKeydown(event: KeyboardEvent, row: V2OrderLine, rowIndex: number): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.runQuantityBlur(row, rowIndex);
            this.focusFieldAt('rate', rowIndex);
            return;
        }
        if (!this.isGridKeyboardNavMode()) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.focusFieldAt('qty', rowIndex + 1, 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.focusFieldAt('qty', rowIndex - 1, -1);
        }
    }

    onRateKeydown(event: KeyboardEvent, _row: V2OrderLine, rowIndex: number): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (this.division === '400') {
                // No disc column: next editable qty (draft) or next rate (price edit).
                if (this.priceEditMode) {
                    this.focusFieldAt('rate', rowIndex + 1, 1);
                } else {
                    this.focusFieldAt('qty', rowIndex + 1, 1);
                }
            } else {
                this.focusFieldAt('disc', rowIndex);
            }
            return;
        }
        if (!this.isGridKeyboardNavMode()) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.focusFieldAt('rate', rowIndex + 1, 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.focusFieldAt('rate', rowIndex - 1, -1);
        }
    }

    onDiscKeydown(event: KeyboardEvent, _row: V2OrderLine, rowIndex: number): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            // Draft: next row Qty. Price edit: Qty locked → next row Rate.
            if (this.priceEditMode) {
                this.focusFieldAt('rate', rowIndex + 1, 1);
            } else {
                this.focusFieldAt('qty', rowIndex + 1, 1);
            }
            return;
        }
        if (!this.isGridKeyboardNavMode()) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.focusFieldAt('disc', rowIndex + 1, 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.focusFieldAt('disc', rowIndex - 1, -1);
        }
    }

    /** Draft bulk-edit or synced Edit Prices — same keyboard nav UX. */
    private isGridKeyboardNavMode(): boolean {
        return this.bulkEditMode || this.priceEditMode;
    }

    private focusFieldAt(field: 'qty' | 'rate' | 'disc', displayIndex: number, direction: 1 | -1 = 1): void {
        const max = this.displayRows.length - 1;
        if (field === 'disc' && this.division === '400') {
            return;
        }
        const cls = field === 'qty' ? '.v2-qty-input' : field === 'rate' ? '.v2-rate-input' : '.v2-disc-input';
        setTimeout(() => {
            const root = this.host.nativeElement;
            let idx = displayIndex;
            while (idx >= 0 && idx <= max) {
                const rowEl = root.querySelector(`tr[data-v2idx="${idx}"]`);
                const input = rowEl?.querySelector(cls) as HTMLInputElement | null;
                if (input) {
                    input.focus?.();
                    input.select?.();
                    return;
                }
                idx += direction;
            }
        });
    }

    private getItemListIndex(row: V2OrderLine): number {
        return (this.itemList || []).findIndex((r) => `${r?.lineNumber}` === `${row?.lineNumber}` && `${r?.itemNumber}` === `${row?.itemNumber}`);
    }

    private getRowKey(row: V2OrderLine): string {
        return `${row?.lineNumber || ''}_${row?.itemNumber || ''}`;
    }

    private matchesGlobalFilter(row: V2OrderLine, term: string): boolean {
        const haystack = [
            row?.lineNumber,
            row?.itemNumber,
            row?.itemName,
            row?.quantity,
            this.getShippedDisplayQty(row),
            row?.rate,
            row?.discount,
            row?.amountAfterDiscount,
            row?.vat,
            row?.amountAfterVat,
            row?.['avlStock'],
            this.getDisplayedAvlStock(row),
            (row as any)?.copyOrderStockError
        ]
            .filter((value) => value !== null && value !== undefined)
            .map((value) => String(value).toLowerCase())
            .join(' ');
        return haystack.includes(term);
    }

    canShowLineTextActions(): boolean {
        if (this.readOnly || this.isReadOnly || this.bulkEditMode) {
            return false;
        }
        const movingStatus = (this.orderMovingStatus || '').toUpperCase();
        return !['WIP', 'INV', 'CAN'].includes(movingStatus);
    }

    shouldHideStockInfo(row: V2OrderLine): boolean {
        if (this.isCreditNoteOrder) {
            return false;
        }
        return effectiveShippedQty(row) <= 0;
    }

    /**
     * `copyOrderStockError` is set during copy-flow prevalidation.
     * If a later stock refresh succeeds (apiStock/avlStock present and no stockError),
     * that old "not verified" message should no longer be shown in V2 UI.
     */
    shouldShowCopyOrderError(row: V2OrderLine): boolean {
        const raw = (row as any)?.copyOrderStockError;
        if (!raw) {
            return false;
        }
        const message = String(raw).toLowerCase();
        const hasResolvedStock = (row as any)?.apiStock != null || (row as any)?.avlStock != null;
        const isCurrentlyHealthy = hasResolvedStock && !(row as any)?.stockError;

        if (message.includes('could not be verified') && isCurrentlyHealthy) {
            return false;
        }
        return true;
    }

    shouldShowStockVerificationError(row: V2OrderLine): boolean {
        if (this.shouldShowCopyOrderError(row)) {
            return false;
        }
        const stockError = !!(row as any)?.stockError;
        const apiStock = (row as any)?.apiStock;
        const hasApiStock = apiStock !== undefined && apiStock !== null && !isNaN(Number(apiStock));
        return stockError && !hasApiStock;
    }

    getStockVerificationMessage(_row: V2OrderLine): string {
        return 'Cannot verify stock because stock count is not available from API.';
    }

    /**
     * Per-row dynamic Aval stock display for the V2 lines grid.
     *
     * Mirrors the logic the sidebar uses (see V1 `qtySetter` and
     * `grid-stock.service.calculateLocalStockForDisplay`): from the raw API
     * stock baseline subtract the sum of same-item draft (status 10/30)
     * quantities. Status 20 lines are already deducted in the API count
     * itself, so they are intentionally excluded from the subtraction.
     */
    getDisplayedAvlStock(row: V2OrderLine): number {
        const apiStock = (row as any)?.apiStock;
        const avlStock = (row as any)?.avlStock;
        const baseline = Number(apiStock != null ? apiStock : avlStock != null ? avlStock : 0);
        const consumed = (this.itemList || [])
            .filter((r: any) => r?.itemNumber === row?.itemNumber && (Number(r?.status) === 10 || Number(r?.status) === 30))
            .reduce((sum: number, r: any) => sum + effectiveShippedQty(r), 0);
        return Math.max(0, baseline - consumed);
    }

    getCompactStockError(errorMessage: string): string {
        const normalized = (errorMessage || '').toLowerCase();
        if (normalized.includes('could not be verified')) {
            return 'Stock not verified';
        }
        if (normalized.includes('insufficient stock')) {
            return 'Insufficient stock';
        }
        return errorMessage || 'Stock error';
    }

    shouldShowStockErrorIcon(errorMessage: string): boolean {
        const normalized = (errorMessage || '').toLowerCase();
        return !normalized.includes('insufficient stock');
    }

    private resolveLnStatRaw(row: V2OrderLine): string | number | null | undefined {
        return row?.m3LineStatus ?? (row as any)?.m3lineStatus ?? (row as any)?.m3_line_status;
    }

    formatLnStat(row: V2OrderLine): string {
        const raw = this.resolveLnStatRaw(row);
        if (raw == null || raw === '') {
            return '-';
        }
        return String(raw);
    }

    isLnStat99(row: V2OrderLine): boolean {
        return String(this.resolveLnStatRaw(row) ?? '').trim() === '99';
    }

    openLineTextWizard(row: V2OrderLine, textType: string): void {
        this.selectedLineForText = row;
        this.selectedLineTextType = textType;
        this.showLineTextWizardDialog = true;
    }

    onLinePrePostComplete(result: PrePostWizardResult): void {
        const lineNo = this.selectedLineForText?.lineNumber?.toString()?.trim();
        const orderNumber = this.orderNumber?.trim();
        const company = this.company || this.inMemoryStorage.get('company') || '';
        const division = this.division || this.inMemoryStorage.get('division') || '';

        if (!lineNo || !company || !division) {
            this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Missing order/line details for line pre/post text.', life: 4000 });
            return;
        }

        if (!orderNumber || orderNumber === 'New Order') {
            this.itemDetailsService.queuePendingLineText(lineNo, result.textType, result.targets);
            const typeLabel = (result.textType || '').toUpperCase().includes('PRE') ? 'Pre-Text' : 'Post-Text';
            this.messageService.add({ severity: 'success', summary: 'Saved', detail: `${typeLabel} added to Line ${lineNo}.`, life: 3500 });
            return;
        }

        this.itemDetailsService.updateOrderLineTextByDocuments(orderNumber, lineNo, company, division, result.textType, result.targets).subscribe({
            next: (message: any) => this.messageService.add({ severity: 'success', summary: 'Saved', detail: typeof message === 'string' ? message : 'Line text updated successfully.', life: 3000 }),
            error: (err) =>
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save line text.', life: 4000 })
        });
    }
}
